import {
  Modal,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useRef, useState } from "react";
import GoogleMapsLoader from "../GoogleMapsLoader/GoogleMapsLoader";

const LocationMapModal = ({ open, onClose, address }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [mapError, setMapError] = useState(null);

  const renderMap = (isGoogleMapsLoaded) => {
    // If the GoogleMapsLoader indicates the API is not yet loaded, show a loading indicator
    if (!isGoogleMapsLoaded) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>جاري تحميل الخريطة...</Typography>
        </Box>
      );
    }

    // If there's a map error, show it
    if (mapError) {
      return (
        <Box sx={{ p: 3, textAlign: "center", color: "error.main" }}>
          <Typography>{mapError}</Typography>
        </Box>
      );
    }

    // If there's no address data, show a message
    if (!address) {
      return <Typography>لا توجد بيانات موقع للعرض</Typography>;
    }

    // Return the map container div
    return (
      <Box
        ref={mapRef}
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: 1,
        }}
      />
    );
  };

  const initializeMap = (isGoogleMapsLoaded) => {
    if (
      !isGoogleMapsLoaded ||
      !mapRef.current ||
      !address ||
      !address.coordinates
    )
      return;

    try {
      // Check if coordinates are valid
      const { coordinates } = address.coordinates;
      if (!coordinates || coordinates.length !== 2) {
        setMapError("إحداثيات غير صالحة");
        return;
      }

      const [longitude, latitude] = coordinates;

      // Initialize the map
      const mapOptions = {
        center: { lat: latitude, lng: longitude },
        zoom: 15,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      };

      mapInstanceRef.current = new window.google.maps.Map(
        mapRef.current,
        mapOptions
      );

      // Add a marker at the address location
      markerRef.current = new window.google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map: mapInstanceRef.current,
        title: address.name || "Location",
      });

      // Add an info window with address details
      const infoContent = `
        <div dir="rtl" style="padding: 10px;">
          <h4>${address.name || "موقع"}</h4>
          <p>${address.city}, ${address.region}</p>
          <p>${address.street}, مبنى ${address.buildingNo}, دور ${
        address.floorNo
      }</p>
          <p>${address.addressSign || ""}</p>
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoContent,
      });

      markerRef.current.addListener("click", () => {
        infoWindow.open(mapInstanceRef.current, markerRef.current);
      });

      // Open info window by default
      infoWindow.open(mapInstanceRef.current, markerRef.current);
    } catch (error) {
      console.error("Error initializing map:", error);
      setMapError("حدث خطأ أثناء تحميل الخريطة. يرجى المحاولة مرة أخرى.");
    }
  };

  // Cleanup function for the map
  const cleanupMap = () => {
    if (mapInstanceRef.current) {
      // Clear any event listeners if needed
      if (markerRef.current) {
        // Clear marker listeners if needed
        // google.maps.event.clearInstanceListeners(markerRef.current);
      }
    }

    // Reset the error state when modal closes
    setMapError(null);
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        cleanupMap();
        onClose();
      }}
      aria-labelledby="location-modal-title"
      aria-describedby="location-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "80%", md: "60%" },
          height: { xs: "60%", sm: "70%", md: "70%" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 2,
          borderRadius: 2,
          outline: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            id="location-modal-title"
            variant="h6"
            component="h2"
            dir="rtl"
          >
            {address ? `موقع: ${address.name || "العنوان"}` : "عرض الموقع"}
          </Typography>
          <IconButton
            onClick={() => {
              cleanupMap();
              onClose();
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: "calc(100% - 50px)",
            borderRadius: 1,
            bgcolor: "#f5f5f5",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <GoogleMapsLoader apiKey="AIzaSyBONrTr5LIsk8KCBZR0ulkvCPgZ9Dd2a3M">
            {({ isLoaded }) => {
              // Initialize map when Google Maps is loaded and modal is open
              if (open && isLoaded && address) {
                // Use setTimeout to ensure the DOM is ready
                setTimeout(() => initializeMap(isLoaded), 300);
              }

              return renderMap(isLoaded);
            }}
          </GoogleMapsLoader>
        </Box>
      </Box>
    </Modal>
  );
};

export default LocationMapModal;
